import { BiDateWindow } from '@euler/model/bi/query';
import { BiBarGap, BiLegendPosition, BiVizType } from '@euler/model/bi/viz';
import {
  BiGalleryCategory,
  BiGalleryCategoryKey,
  BiGalleryDashboardCard,
  BiGalleryVariant,
} from '../functions/biGalleryTypes';
import {
  cents,
  kGalleryFields as f,
  galleryReady,
  galleryResult,
  kGalleryLongItems,
} from './biGalleryFields';

const technicians: [string, number, number][] = [
  ['王建国', 128, 142],
  ['李晓明', 121, 118],
  ['张伟', 109, 131],
  ['刘洋', 102, 96],
  ['陈志强', 88, 109],
  ['杨帆', 74, 81],
];

const months: BiGalleryDashboardCard = {
  surface: 'dashboard',
  title: '本月与上月各技师施工数',
  viz: {
    type: BiVizType.Pyramid,
    x: f.technician.key,
    series: [
      { key: f.lastMonthJobs.key, name: '上月' },
      { key: f.thisMonthJobs.key, name: '本月' },
    ],
  },
  data: galleryReady(
    galleryResult(
      [f.technician, f.lastMonthJobs, f.thisMonthJobs],
      technicians.map(([name, last, now]) => [name, last, now]),
    ),
  ),
};

const brands: [string, number, number][] = [
  ['大众', 46, 182],
  ['丰田', 38, 164],
  ['本田', 31, 121],
  ['宝马', 22, 97],
  ['梅赛德斯-奔驰', 19, 88],
  ['比亚迪', 41, 63],
];

const customers: BiGalleryDashboardCard = {
  surface: 'dashboard',
  title: '各品牌新老客户',
  viz: {
    type: BiVizType.Pyramid,
    x: f.brand.key,
    series: [{ key: f.newCustomers.key }, { key: f.returning.key }],
  },
  data: galleryReady(
    galleryResult(
      [f.brand, f.newCustomers, f.returning],
      brands.map(([brand, fresh, back]) => [brand, fresh, back]),
    ),
  ),
};

const kJobs = ['机油保养', '刹车片', '四轮定位', '空调清洗', '轮胎更换'];

function splitCard(
  title: string,
  periods: string[],
  counts: number[][],
  window?: BiDateWindow,
): BiGalleryDashboardCard {
  return {
    surface: 'dashboard',
    title,
    viz: {
      type: BiVizType.Pyramid,
      x: f.jobName.key,
      series: [{ key: f.jobs.key }],
      seriesBy: f.month.key,
    },
    data: galleryReady(
      galleryResult(
        [f.jobName, f.month, f.jobs],
        // The newer period first, as a sort by count often returns it: the
        // chart still puts the earlier one on the left.
        [...periods]
          .reverse()
          .flatMap(period =>
            kJobs.map((job, j) => [
              job,
              period,
              counts[periods.indexOf(period)][j],
            ]),
          ),
        window,
      ),
    ),
  };
}

const split = splitCard(
  '本月与上月各施工项目数',
  ['2026-09', '2026-10'],
  [
    [64, 41, 28, 22, 37],
    [71, 38, 33, 19, 45],
  ],
);

const parts = [52800, 31600, 24800, 18200, 15400, 9600];
const labor = [18400, 22300, 9900, 12600, 6100, 7800];

const money: BiGalleryDashboardCard = {
  surface: 'dashboard',
  title: '各项目配件与工时营收',
  viz: {
    type: BiVizType.Pyramid,
    x: f.item.key,
    series: [{ key: f.partsRevenue.key }, { key: f.laborRevenue.key }],
    style: { dataLabels: true },
  },
  data: galleryReady(
    galleryResult(
      [f.item, f.partsRevenue, f.laborRevenue],
      kGalleryLongItems.map((item, i) => [
        item,
        cents(parts[i]),
        cents(labor[i]),
      ]),
    ),
  ),
};

function withStyle(
  key: string,
  name: string,
  note: string,
  card: BiGalleryDashboardCard,
  style: NonNullable<BiGalleryDashboardCard['viz']['style']>,
): BiGalleryVariant {
  return { key, name, note, card: { ...card, viz: { ...card.viz, style } } };
}

export const kPyramidCategory: BiGalleryCategory = {
  key: BiGalleryCategoryKey.Pyramid,
  name: '金字塔图',
  description: '本期与上期、两个指标左右对比、金额标签、柱形样式',
  preview: months,
  variants: [
    {
      key: 'periods',
      name: '本月与上月',
      note: '组合数据：同一数据集的两期',
      card: months,
    },
    {
      key: 'measures',
      name: '两个指标',
      note: '新客户在左，老客户在右',
      card: customers,
    },
    {
      key: 'split',
      name: '按月拆成两边',
      note: 'seriesBy = 月份，较早的一期在左',
      card: split,
    },
    {
      key: 'money',
      name: '金额与数据标签',
      note: '长项目名、dataLabels = true',
      card: money,
    },
    withStyle(
      'bars',
      '大圆角、窄间距',
      'bar.cornerRadius = 8，gap = narrow',
      customers,
      { bar: { cornerRadius: 8, gap: BiBarGap.Narrow } },
    ),
    withStyle('legend', '显示图例', 'legend.position = top', months, {
      legend: { show: true, position: BiLegendPosition.Top },
    }),
    {
      key: 'one-side',
      name: '只有一边有数据',
      note: '本月还没有记录：右边没有柱子，两边的标题都在',
      card: splitCard(
        '本月与上月各施工项目数',
        ['2026-09'],
        [[64, 41, 28, 22, 37]],
        { from: '2026-09-01', to: '2026-10-17' },
      ),
    },
  ],
};
